/**
 * Turning a server-held price into something a learner can read. Both helpers
 * derive everything from the amounts they are handed, so they say whatever the
 * real prices say — there is no plan data written down here.
 */

/**
 * A Stripe amount to a display string. Stripe counts in the currency's minor
 * unit, and how many of those make a major unit varies — 100 cents to the
 * dollar, but the yen has no subdivision at all — so the divisor is read off
 * the currency rather than assumed to be 100.
 */
// Constructing an Intl.NumberFormat costs more than the formatting it does,
// and this is called on every interval toggle, so they are built once per
// (currency, whole?) pair and kept.
const formatters = new Map<string, Intl.NumberFormat>();

const formatterFor = (currency: string, minimumFractionDigits: number) => {
  const key = `${currency}:${minimumFractionDigits}`;
  const existing = formatters.get(key);
  if (existing) return existing;

  const created = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
  });
  formatters.set(key, created);
  return created;
};

export const formatAmount = (amountMinor: number, currency: string): string => {
  const code = currency.toUpperCase();
  const fractionDigits = formatterFor(code, 0).resolvedOptions()
    .maximumFractionDigits;

  const amount = amountMinor / 10 ** fractionDigits;

  // $120 rather than $120.00, but $12.50 keeps its cents.
  return formatterFor(
    code,
    Number.isInteger(amount) ? 0 : fractionDigits
  ).format(amount);
};

/**
 * What a year on the yearly price saves against paying the monthly price
 * twelve times, to the nearest percent.
 *
 * Undefined when it saves nothing, and undefined when either amount is
 * missing: `unitAmount` is a display cache the server refreshes at checkout,
 * so a null is "we don't know", not zero. A badge computed from a guess would
 * advertise a discount nobody agreed to.
 */
export const getYearlySavingPercent = (
  monthlyMinor: number | null | undefined,
  yearlyMinor: number | null | undefined
): number | undefined => {
  if (monthlyMinor == null || yearlyMinor == null) return undefined;

  const yearAtMonthlyRate = monthlyMinor * 12;
  if (yearAtMonthlyRate <= yearlyMinor) return undefined;

  return Math.round(
    ((yearAtMonthlyRate - yearlyMinor) / yearAtMonthlyRate) * 100
  );
};
